import type { BulleEnrichie, Vivier } from '../partage/api/types';
import { attendLaTache, attendLeMondeLigne, avantLeCourt, ceQuiPresseLigne, LIGNE_ATTEND, proprietesEnMots, reponseLe } from '../partage/vocabulaire';

/**
 * Un projet ouvert au téléphone, en arborescence (chantier telephone-projet, 02, maquette
 * `app-au-telephone.html`, `projet()`, l'arbre). Les règles, sans rendu ; testées dans
 * `tests/client/projets/arbreAuPouce.test.ts`.
 *
 * - Une bulle qui attend le monde (sa situation : en attente, ou à vérifier) se dit en ocre,
 *   jamais en rouge.
 * - Au bout d'un fichier, à droite : la réponse attendue, sinon l'échéance, sinon sa durée.
 * - « Montrer ce qui est fait » : oui, les faites restent barrées à leur place ; non, elles
 *   partent de l'arbre.
 * - Ce qui est déplié dans un projet, et le réglage, tiennent le temps de la session : on
 *   revient d'un détail sur le même arbre.
 */

type BulleQuiAttend = Pick<BulleEnrichie, 'situation'>;

export function attendLeMonde(bulle: BulleQuiAttend): boolean {
  return bulle.situation.nom === 'en_attente' || bulle.situation.nom === 'a_verifier';
}

type BulleAuBout = Pick<BulleEnrichie, 'situation' | 'etat' | 'dateEcheance' | 'effortMin'>;

/** « réponse le 25/10 », « avant le 30/10 », « 2 h », ou rien. */
export function ceQueDitLeBout(bulle: BulleAuBout): string | null {
  if (attendLeMonde(bulle) || bulle.etat === 'en_attente_confirmation') {
    return bulle.situation.attenduLe ? reponseLe(bulle.situation.attenduLe) : LIGNE_ATTEND;
  }
  if (bulle.dateEcheance) return avantLeCourt(bulle.dateEcheance);
  return proprietesEnMots(bulle, ['duree'])[0] ?? null;
}

/**
 * Le détail d'une branche (chantier telephone-projet, 03) : sous le titre d'une tâche, en gris,
 * son lieu, sinon ce qui la retient ; sinon rien.
 */
export function ligneGriseDuDetail(bulle: Pick<BulleEnrichie, 'lieuTexte' | 'retenuePar'>): string | null {
  if (bulle.lieuTexte) return bulle.lieuTexte;
  const retenue = bulle.retenuePar[0];
  return retenue ? attendLaTache(retenue.titre) : null;
}

/** Ce que l'arbre montre d'une rangée de sœurs, selon « Montrer ce qui est fait ». */
export function montreesDansLArbre<B extends { etat: string }>(bulles: readonly B[], montrerCeQuiEstFait: boolean): B[] {
  return montrerCeQuiEstFait ? [...bulles] : bulles.filter((b) => b.etat !== 'fait');
}

/** Une ligne du post-it « Ce qui presse » ; `monde` : ce qui attend le monde, écrit plus petit. */
export interface LigneQuiPresse {
  id: string;
  texte: string;
  monde: boolean;
}

/**
 * « Ce qui presse » sous l'arbre : ce que le serveur range en Prioritaire pour ce projet, puis
 * ce qui attend le monde (À vérifier). Vide : pas de post-it.
 */
export function lignesDeCeQuiPresse(vivier: Pick<Vivier, 'prioritaire' | 'aVerifier'>): LigneQuiPresse[] {
  return [
    ...vivier.prioritaire.map((l) => ({ id: l.id, texte: ceQuiPresseLigne(l.titre, l.pourquoi || null), monde: false })),
    ...vivier.aVerifier.map((l) => ({
      id: l.id,
      texte: attendLeMondeLigne(l.titre, l.situation.attenduLe ? reponseLe(l.situation.attenduLe) : null),
      monde: true,
    })),
  ];
}

/* ── Ce qui tient le temps de la session ── */

const deplieesParProjet = new Map<string, ReadonlySet<string>>();
let montrerLesFaites = true;

/** Les branches dépliées d'un projet ; aucune à la première visite. */
export function deplieesDuProjet(projet: string): ReadonlySet<string> {
  return deplieesParProjet.get(projet) ?? new Set();
}

/** Déplier ou replier une branche ; rend ce qui est déplié après. */
export function basculerLaBranche(projet: string, branche: string): ReadonlySet<string> {
  const apres = new Set(deplieesDuProjet(projet));
  if (apres.has(branche)) apres.delete(branche);
  else apres.add(branche);
  deplieesParProjet.set(projet, apres);
  return apres;
}

/** « Montrer ce qui est fait », oui par défaut (comme la maquette). */
export function montrerCeQuiEstFait(): boolean {
  return montrerLesFaites;
}

export function reglerCeQuiEstFait(oui: boolean): void {
  montrerLesFaites = oui;
}
